import React, { lazy, Suspense } from 'react';
import { Box, CircularProgress } from '@mui/material';

/**
 * Default loading component shown while lazy-loaded components are being fetched
 */
const DefaultLoadingComponent: React.FC = () => (
  <Box sx={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    minHeight: 100
  }}>
    <CircularProgress sx={{ color: 'var(--primary-colour)' }} />
  </Box>
);

/**
 * Helper function to lazy load components with a suspense fallback
 * @param importFunc - Dynamic import function for the component
 * @param LoadingComponent - Optional custom loading component
 * @returns Lazy loaded component with suspense
 */
export function lazyLoad<T extends React.ComponentType<any>>(
  importFunc: () => Promise<{ default: T }>,
  LoadingComponent: React.ComponentType = DefaultLoadingComponent
): React.ComponentType<React.ComponentProps<T>> {
  const LazyComponent = lazy(importFunc);

  return (props: React.ComponentProps<T>) => (
    <Suspense fallback={<LoadingComponent />}>
      <LazyComponent {...props} />
    </Suspense>
  );
}
